import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import {
	apiServices,
	DELETED_ACCOUNT_SESSION_MESSAGE,
	deleteToken,
	saveAuthSessionMessage,
	setToken,
	shouldTreatSessionAsExpired,
} from "../utilities/ApiServices";

const CHECK_ACCOUNT_EVERY_MS = 8000;

const sendToLogin = () => {
	window.localStorage.clear();
	deleteToken();
	saveAuthSessionMessage(DELETED_ACCOUNT_SESSION_MESSAGE);
	if (!window.location.pathname.startsWith("/auth/login")) {
		window.location.href = "/auth/login";
	}
};

export const AccountSessionWatcher = ({
	children,
}: {
	children: ReactNode;
}) => {
	const checkingRef = useRef(false);

	useEffect(() => {
		let cancelled = false;

		const checkAccountStillExists = async () => {
			if (checkingRef.current) return;
			if (window.location.pathname.startsWith("/auth")) return;
			if (window.location.pathname === "/like-email-settings") return;
			const storedToken = window.localStorage.getItem("userToken");
			if (!storedToken) return;

			checkingRef.current = true;
			try {
				// On a fresh page load this watcher can run before the normal session
				// renewal. Attach the saved token first so a valid member is not
				// mistaken for a deleted account and signed out.
				setToken(storedToken);
				await apiServices.get("/auth/info");
			} catch (error: any) {
				const statusCode = error?.response?.status;
				const data = error?.response?.data;
				const message =
					typeof data?.message === "string"
						? data.message
						: typeof data?.error === "string"
							? data.error
							: "";

				if (!cancelled && shouldTreatSessionAsExpired(statusCode, message)) {
					sendToLogin();
				}
			} finally {
				checkingRef.current = false;
			}
		};

		const intervalId = window.setInterval(
			checkAccountStillExists,
			CHECK_ACCOUNT_EVERY_MS,
		);
		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				void checkAccountStillExists();
			}
		};

		document.addEventListener("visibilitychange", handleVisibilityChange);
		void checkAccountStillExists();

		return () => {
			cancelled = true;
			window.clearInterval(intervalId);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, []);

	return <>{children}</>;
};
